import {
	ReactElement,
	Ref,
	useCallback,
	useMemo
} from "react";
import resultViewStyles from "../ResultView/ResultView.module.less";
import {
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import {
	SearchNodeModel,
	GlobalSearchTermModel
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import {
	NoResults
 } from "client/Components";
import { RecentSearchListView } from "./RecentSearchListView/RecentSearchListView";
import { localize } from "client_legacy/ndaw/culture";
import { GlobalSearchNodeTabs } from "../ResultView/GlobalSearchNodeTabs";
import { FrequentlyUsedAppList, FrequentlyUsedAppsHandle } from "./FrequentlyUsedAppList";
import { ResultSectionLayout } from "../ResultSectionLayout/ResultSectionLayout";
import { useFrequentlyUsedTiles } from "client/Features/Layout/FrequentlyUsedTiles/useFrequentlyUsedTiles";
import { FrequentlyUsedTileModel } from "client/Api/Workspace/workspace";
import { GlobalSearchItemPersistedModel } from "client/Features/Search/RecentSearch/Persistence/models";
import { RecentlyOpenedView } from "./RecentlyOpenedView/RecentlyOpenedView";
import { TimeZone } from "client/DateAndTime/timezones";
import { ContactSource } from "client/Api/Email/contacts";
import { TabbableItem } from "../SearchAndResults/SearchAndResults";
import { GridNavigationDirection } from "client/Hooks/useKeyboardGridNavigation";
import { Key } from "client/Misc/commonTypes";

export type RecentSearchViewProps = {
	nodes: readonly SearchNodeModel[];
	selectedNode: SearchNodeModel | null;
	onSelectedNodeChanged(newScope: SearchNodeModel | null): void;
	onFocusInput(): void;
	recentSearchQueries: readonly GlobalSearchTermModel[];
	onDeleteSearchQuery(id: number): void;
	onClearSearchQueries: () => void;
	onRemoveRecentlyOpenedItem: (item: GlobalSearchItemPersistedModel) => void;
	onClearRecentlyOpenedItems: () => void;
	onSearch(searchTerm: string): void;
	onOpenContactDetails: (id: string, source: ContactSource) => void;
	onInvokeFrequentlyUsedTile: (item: FrequentlyUsedTileModel) => void;
	onDismiss: () => void;
	recentSearchResults: readonly GlobalSearchItemPersistedModel[];
	timezone: TimeZone;
	tabbableItem: TabbableItem | null;
	onTabbableItemChange: (item:TabbableItem) => void;
	frequentlyUsedAppsRef: Ref<FrequentlyUsedAppsHandle>;
	onFocusItemAtIndex: (index: number) => void;
};

export const RecentSearchView = (props: RecentSearchViewProps): ReactElement => {
	const {
		nodes,
		selectedNode,
		onSelectedNodeChanged,
		onFocusInput,
		recentSearchQueries,
		onDeleteSearchQuery,
		onClearSearchQueries,
		onRemoveRecentlyOpenedItem,
		onClearRecentlyOpenedItems,
		onSearch,
		onOpenContactDetails,
		onInvokeFrequentlyUsedTile,
		onDismiss,
		onTabbableItemChange,
		recentSearchResults,
		timezone,
		tabbableItem,
		frequentlyUsedAppsRef,
		onFocusItemAtIndex
	} = props;

	const [ items ] = useFrequentlyUsedTiles();

	const frequentlyUsedTiles = useMemo(
		(): readonly FrequentlyUsedTileModel[] | undefined => items
			? items.slice(0, MAX_FREQUENTLY_USED_TILES)
			: undefined,
		[items]
	);

	const recentSearchResultsToRender = useMemo(
		(): readonly GlobalSearchItemPersistedModel[] => {
			if (recentSearchResults.length > MAX_RECENT_SEARCH_RESULTS) {
				return recentSearchResults.slice(0, MAX_RECENT_SEARCH_RESULTS);
			}

			return recentSearchResults;
		},
		[recentSearchResults]
	);

	const scopeChangedHandler = (node: SearchNodeModel | null): void => {
		onSelectedNodeChanged(node);
		onFocusInput();
	};

	const hasRecentSearchResults: boolean = recentSearchResultsToRender.length !== 0;
	const hasRecentSearchQueries: boolean = recentSearchQueries.length !== 0;

	const lastResultItemIndex: number = recentSearchResultsToRender.length + recentSearchQueries.length - 1;

	const frequentlyUsedAppListOutOfBoundsHandler = useCallback(
		(direction: GridNavigationDirection): void => {
			if (direction === Key.ArrowLeft || direction === Key.ArrowUp) {
				onTabbableItemChange({ index: lastResultItemIndex, kind: "result-item" });

				if (lastResultItemIndex === -1) {
					onFocusInput();
					return;
				}

				onFocusItemAtIndex(lastResultItemIndex);
			}
		},
		[
			lastResultItemIndex,
			onFocusInput,
			onFocusItemAtIndex,
			onTabbableItemChange
		]
	);

	return (
		<>
			<div
				{...setTrackingId("recent-search-view")}
				className={resultViewStyles.root}
			>
				<div
					{...setTrackingId("header")}
					className={resultViewStyles.header}
				>
					<div
						{...setTrackingId("navigation-bar")}
						className={resultViewStyles.navigationBarWrapper}
					>
						<GlobalSearchNodeTabs
							selectedNode={selectedNode}
							onSelectedNodeChange={scopeChangedHandler}
							nodes={nodes}
							trackingId="recent-search-node-tabs"
						/>
					</div>
				</div>
				<div
					{...setTrackingId("body")}
					className={resultViewStyles.body}
				>
					{hasRecentSearchResults && (
						<RecentlyOpenedView
							onOpenContactDetails={onOpenContactDetails}
							recentSearchResults={recentSearchResultsToRender}
							onRemove={onRemoveRecentlyOpenedItem}
							onClear={onClearRecentlyOpenedItems}
							timezone={timezone}
							onDismiss={onDismiss}
							tabbableIndex={tabbableItem?.kind === "result-item" ? tabbableItem.index : null}
						/>
					)}
					{hasRecentSearchQueries && (
						<RecentSearchListView
							recentSearchQueries={recentSearchQueries}
							navigationStartingIndex={recentSearchResultsToRender.length}
							onDelete={onDeleteSearchQuery}
							onClear={onClearSearchQueries}
							onSearch={onSearch}
							tabbableIndex={tabbableItem?.kind === "result-item" ? tabbableItem.index : null}
						/>
					)}
					{!hasRecentSearchResults && !hasRecentSearchQueries && (
						<NoResults
							trackingId="enter-search-term"
							icon="magnifying-glass"
							title={localize("_EnterASearchTerm")}
							message={localize("_PleaseEnterASearchTermToBeginYourSearch")}
						/>
					)}
					{(isNil(frequentlyUsedTiles) || frequentlyUsedTiles.length > 0) && (
						<ResultSectionLayout
							trackingId="frequently-used-apps"
							title={localize("_FrequentlyUsedApps")}
						>
							<FrequentlyUsedAppList
								ref={frequentlyUsedAppsRef}
								onOutOfBounds={frequentlyUsedAppListOutOfBoundsHandler}
								tabbableIndex={tabbableItem?.kind === "frequently-used-app" ? tabbableItem.index : null}
								onTabbableIndexChange={(index: number): void => onTabbableItemChange({
									index,
									kind: "frequently-used-app"
								})}
								{...(isNotNil(frequentlyUsedTiles)
									? {
										status: "ready",
										items: frequentlyUsedTiles,
										onInvoke: onInvokeFrequentlyUsedTile
									}
									: {
										status: "loading"
									}
								)}
							/>
						</ResultSectionLayout>
					)}
				</div>
			</div>
		</>
	);
};
RecentSearchView.displayName = "RecentSearchView";

export const MAX_FREQUENTLY_USED_TILES = 8;
export const MAX_RECENT_SEARCH_RESULTS = 3;
